import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronRight } from "lucide-react";
import Logo from "@/components/layout/Logo";
import { primaryNav } from "@/config/site.config";
import { buildWhatsAppUrl, defaultEnquiryMessage } from "@/utilities/whatsapp";
import { trackEvent, AnalyticsEvents } from "@/utilities/analytics";

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const quoteHref = buildWhatsAppUrl(defaultEnquiryMessage);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const linkClass = ({ isActive }) =>
        `relative font-mono text-[12px] uppercase tracking-[0.22em] py-2 transition-colors ${
            isActive
                ? "text-cs-orange"
                : "text-white hover:text-cs-orange"
        }`;

    return (
        <>
            <header
                className={`sticky top-0 z-40 border-b border-white/10 transition-colors ${
                    scrolled ? "bg-cs-navy/95 backdrop-blur-md" : "bg-cs-navy"
                }`}
                data-testid="site-header"
            >
                <div className="cs-container flex items-center justify-between h-16 md:h-20">
                    <Logo variant="dark" />
                    <nav
                        className="hidden lg:flex items-center gap-8"
                        aria-label="Primary"
                        data-testid="primary-nav"
                    >
                        {primaryNav.map((item) => (
                            <NavLink
                                key={item.href}
                                to={item.href}
                                end={item.href === "/"}
                                className={linkClass}
                                data-testid={`nav-link-${item.href.replace(/\//g, "") || "home"}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="hidden md:flex items-center gap-3">
                        <a
                            href={quoteHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                                trackEvent(AnalyticsEvents.WHATSAPP_CLICK, { source: "header-cta" })
                            }
                            className="cs-btn-primary text-xs px-5 py-3"
                            data-testid="header-whatsapp-quote-btn"
                        >
                            <MessageCircle className="w-4 h-4" strokeWidth={2} />
                            Get a Container Quote
                        </a>
                    </div>
                    <button
                        type="button"
                        aria-label="Open menu"
                        onClick={() => setOpen(true)}
                        className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-white/25 text-white hover:border-cs-orange hover:text-cs-orange transition-colors"
                        data-testid="mobile-menu-open-btn"
                    >
                        <Menu size={20} strokeWidth={2} />
                    </button>
                </div>
            </header>

            {open && (
                <div
                    className="fixed inset-0 z-50 lg:hidden"
                    role="dialog"
                    aria-modal="true"
                    data-testid="mobile-menu-drawer"
                >
                    <div
                        className="absolute inset-0 bg-black/60"
                        onClick={() => setOpen(false)}
                        aria-hidden="true"
                    />
                    <div className="absolute right-0 top-0 h-full w-[86%] max-w-sm bg-cs-navy text-white cs-corrugated flex flex-col">
                        <div className="flex items-center justify-between border-b border-white/10 px-5 h-16">
                            <Logo variant="dark" withTag={false} size="sm" />
                            <button
                                type="button"
                                aria-label="Close menu"
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center justify-center w-10 h-10 border border-white/25 hover:border-cs-orange hover:text-cs-orange transition-colors"
                                data-testid="mobile-menu-close-btn"
                            >
                                <X size={20} strokeWidth={2} />
                            </button>
                        </div>
                        <nav className="flex-1 overflow-y-auto px-5 py-6" aria-label="Mobile primary">
                            <ul className="space-y-1">
                                {primaryNav.map((item) => (
                                    <li key={item.href}>
                                        <NavLink
                                            to={item.href}
                                            end={item.href === "/"}
                                            onClick={() => setOpen(false)}
                                            className={({ isActive }) =>
                                                `group flex items-center justify-between border-b border-white/10 py-4 font-heading text-2xl uppercase tracking-tight transition-colors ${
                                                    isActive
                                                        ? "text-cs-orange"
                                                        : "text-white hover:text-cs-orange"
                                                }`
                                            }
                                            data-testid={`mobile-nav-link-${item.href.replace(/\//g, "") || "home"}`}
                                        >
                                            <span>{item.label}</span>
                                            <ChevronRight
                                                size={18}
                                                strokeWidth={2}
                                                className="opacity-40 group-hover:opacity-100 group-hover:text-cs-orange transition"
                                            />
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                            <div className="mt-8 space-y-3">
                                <a
                                    href={quoteHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                        setOpen(false);
                                        trackEvent(AnalyticsEvents.WHATSAPP_CLICK, {
                                            source: "mobile-menu",
                                        });
                                    }}
                                    className="cs-btn-primary w-full"
                                    data-testid="mobile-menu-whatsapp-btn"
                                >
                                    <MessageCircle className="w-4 h-4" strokeWidth={2} />
                                    Get a Container Quote
                                </a>
                                <Link
                                    to="/contact-us"
                                    onClick={() => setOpen(false)}
                                    className="cs-btn-ghost w-full"
                                    data-testid="mobile-menu-contact-link"
                                >
                                    Contact Details
                                </Link>
                            </div>
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
}
